import '../../styles/editcase/TimelineTab.css';
import { TIMELINE_CATEGORY_LABELS } from '../../utils/activeCarePlan';
import { effectiveStatus } from '../../utils/timelineReview';
import ReviewStamp from './ReviewStamp';

const STATUS_LABELS = {
    active: 'Active',
    completed: 'Completed',
    discontinued: 'Discontinued',
    unknown: '',
};

const CORRECTABLE_STATUSES = ['active', 'completed', 'discontinued', 'unknown'];

function formatDateRange(event) {
    const start = event.date || '';
    if (!start || !event.end_date) return start;
    return `${start} — ${event.end_date}`;
}

function ReviewControls({ event, review, onReview }) {
    if (!onReview) return null;
    const entry = (review || {})[event.id] || {};

    if (entry.decision === 'dismissed') {
        return (
            <div className="timeline-item-review">
                <span className="timeline-review-state timeline-review-state--dismissed">Dismissed</span>
                <button
                    type="button"
                    className="timeline-review-button"
                    onClick={() => onReview(event.id, null)}
                >
                    Undo
                </button>
            </div>
        );
    }

    const confirmed = entry.decision === 'confirmed';
    const status = effectiveStatus(event, review);
    return (
        <div className="timeline-item-review">
            <button
                type="button"
                className={`timeline-review-button ${confirmed ? 'timeline-review-button--confirmed' : ''}`}
                onClick={() => onReview(
                    event.id,
                    confirmed ? null : 'confirmed',
                    confirmed ? null : (entry.corrected_status ?? null),
                )}
            >
                {confirmed ? '✓ Confirmed' : 'Confirm'}
            </button>
            <button
                type="button"
                className="timeline-review-button timeline-review-button--dismiss"
                onClick={() => onReview(event.id, 'dismissed')}
            >
                Dismiss
            </button>
            <label className="timeline-review-status-label">
                Status
                <select
                    className="timeline-review-status-select"
                    value={status}
                    onChange={(e) => onReview(
                        event.id,
                        'confirmed',
                        e.target.value === event.status ? null : e.target.value,
                    )}
                >
                    {CORRECTABLE_STATUSES.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s] || 'Unknown'}</option>
                    ))}
                </select>
            </label>
        </div>
    );
}

function TimelineItem({ event, review, onReview }) {
    const entry = (review || {})[event.id] || {};
    const dismissed = entry.decision === 'dismissed';
    const status = effectiveStatus(event, review);
    return (
        <li
            className={
                `timeline-item timeline-item--${event.category}` +
                (dismissed ? ' timeline-item--dismissed' : '')
            }
        >
            <div className="timeline-item-date">{formatDateRange(event)}</div>
            <div className="timeline-item-body">
                <div className="timeline-item-meta">
                    <span className={`timeline-badge timeline-badge--${event.category}`}>
                        {TIMELINE_CATEGORY_LABELS[event.category] || event.category}
                    </span>
                    {status && status !== 'unknown' && (
                        <span className={`timeline-status timeline-status--${status}`}>
                            {STATUS_LABELS[status] || status}
                        </span>
                    )}
                </div>
                <div className="timeline-item-title">{event.title}</div>
                {event.description && (
                    <div className="timeline-item-description">{event.description}</div>
                )}
                {event.citation && (
                    <div className="timeline-item-citation">
                        <em>"{event.citation}"</em>
                        {event.source_document && (
                            <span className="timeline-item-source">
                                {' — '}
                                {event.source_document}
                                {event.page && `, p. ${event.page}`}
                            </span>
                        )}
                    </div>
                )}
                <ReviewControls event={event} review={review} onReview={onReview} />
                {entry.decision && <ReviewStamp review={entry.review} />}
            </div>
        </li>
    );
}

/**
 * Read-only chronological timeline of all extracted events, with optional
 * per-event review controls. Undated events (chronic co-morbidities like
 * anemia) render in a separate section after the dated history so they
 * stay visible. Dismissed events stay visible but muted.
 *
 * @param {Object}   props
 * @param {Array}    props.events   – Timeline events from the case doc.
 * @param {Object}   props.review   – timeline_review map keyed by event id.
 * @param {function} props.onReview – (eventId, decision, correctedStatus);
 *                                    omit for a read-only timeline.
 * @param {function} props.onConfirmAll – bulk-confirm handler; omit to hide.
 */
export default function TimelineTab({ events, review, onReview, onConfirmAll }) {
    if (!events || events.length === 0) {
        return (
            <div className="timeline-tab timeline-tab--empty">
                <p>No timeline events extracted yet.</p>
            </div>
        );
    }

    const dated = events.filter((e) => e && e.date);
    const undated = events.filter((e) => e && !e.date);

    // Lexicographic sort handles mixed ISO precisions (YYYY, YYYY-MM, YYYY-MM-DD).
    // Year-only "2024" sorts AFTER "2023-12-31" but BEFORE any "2024-MM-DD" — acceptable
    // for clinical handoff use; full-day precision events stay in the right order.
    const sorted = [...dated].sort((a, b) =>
        (a.date || '').localeCompare(b.date || '')
    );

    return (
        <div className="timeline-tab">
            {onConfirmAll && (
                <div className="timeline-tab-actions">
                    <button
                        type="button"
                        className="timeline-review-button"
                        onClick={() => onConfirmAll(events)}
                    >
                        Confirm all
                    </button>
                </div>
            )}
            {sorted.length > 0 && (
                <ol className="timeline-list">
                    {sorted.map((event) => (
                        <TimelineItem key={event.id} event={event} review={review} onReview={onReview} />
                    ))}
                </ol>
            )}
            {undated.length > 0 && (
                <section className="timeline-undated">
                    <h4 className="timeline-undated-heading">Ongoing / undated</h4>
                    <ol className="timeline-list">
                        {undated.map((event) => (
                            <TimelineItem key={event.id} event={event} review={review} onReview={onReview} />
                        ))}
                    </ol>
                </section>
            )}
        </div>
    );
}
